import React from "react";

const ArticleDetailSkeleton = () => {
  const styles = {
    container: {
      display: "flex",
      minHeight: "100vh",
      fontFamily: "system-ui, -apple-system, sans-serif",
      backgroundColor: "#f5f5f5",
    },
    leftPanel: {
      flex: 1,
      backgroundColor: "#fff",
      padding: "32px",
      overflowY: "auto",
    },
    rightPanel: {
      width: "424px",
      backgroundColor: "#fff",
      padding: "32px",
      borderLeft: "1px solid #e0e0e0",
      overflowY: "auto",
    },
    skeleton: {
      backgroundColor: "#e0e0e0",
      borderRadius: "4px",
      animation: "pulse 1.5s ease-in-out infinite",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    iconButtons: {
      display: "flex",
      gap: "16px",
      marginBottom: "24px",
    },
    divider: {
      height: "1px",
      backgroundColor: "#e0e0e0",
      margin: "24px 0",
    },
    checklistItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    },
    infoRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      gap: "16px",
    },
    infoLabel: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flex: "0 0 auto",
    },
    infoValue: {
      flex: 1,
      minWidth: 0,
    },
    commentCard: {
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "16px",
      marginTop: "12px",
    },
    commentHeader: {
      display: "flex",
      gap: "12px",
      marginBottom: "12px",
    },
  };

  const Skeleton = ({ width, height, variant = "text", style = {} }) => {
    const baseStyle = {
      ...styles.skeleton,
      width: typeof width === "number" ? `${width}px` : width,
      height: `${height}px`,
      ...style,
    };

    if (variant === "circular") {
      baseStyle.borderRadius = "50%";
    } else if (variant === "rectangular") {
      baseStyle.borderRadius = "4px";
    }

    return <div style={baseStyle}></div>;
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      <div style={styles.container}>
        {/* Left Content Panel */}
        <div style={styles.leftPanel}>
          {/* Header */}
          <div style={styles.header}>
            <Skeleton width="60%" height={40} />
            <Skeleton width={80} height={24} />
          </div>

          <div style={styles.iconButtons}>
            <Skeleton width={32} height={32} variant="circular" />
            <Skeleton width={32} height={32} variant="circular" />
            <Skeleton width={32} height={32} variant="circular" />
          </div>

          <div style={styles.divider}></div>

          <Skeleton width={150} height={20} style={{ marginBottom: "12px" }} />
          <Skeleton width="100%" height={16} style={{ marginBottom: "8px" }} />
          <Skeleton width="95%" height={16} style={{ marginBottom: "24px" }} />

          <Skeleton width={180} height={20} style={{ marginBottom: "12px" }} />
          <Skeleton width="90%" height={16} style={{ marginBottom: "8px" }} />
          <Skeleton width="85%" height={16} style={{ marginBottom: "16px" }} />

          <Skeleton width={200} height={16} style={{ marginBottom: "6px" }} />
          <Skeleton width={120} height={16} style={{ marginBottom: "6px" }} />
          <Skeleton width={180} height={16} style={{ marginBottom: "6px" }} />
          <Skeleton width={100} height={16} style={{ marginBottom: "6px" }} />
          <Skeleton width={120} height={16} style={{ marginBottom: "6px" }} />
          <Skeleton width={150} height={16} style={{ marginBottom: "24px" }} />

          <Skeleton width={250} height={20} style={{ marginBottom: "12px" }} />
          <Skeleton width="100%" height={16} style={{ marginBottom: "8px" }} />
          <Skeleton width="92%" height={16} style={{ marginBottom: "16px" }} />

          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} style={styles.checklistItem}>
              <Skeleton width={18} height={18} variant="circular" />
              <Skeleton width={200} height={16} />
            </div>
          ))}

          {/* Footer note */}
          <div style={{ marginTop: "24px" }}>
            <Skeleton width="95%" height={16} style={{ marginBottom: "8px" }} />
            <Skeleton width="30%" height={16} />
          </div>
        </div>

        {/* Right Info Panel */}
        <div style={styles.rightPanel}>
          <Skeleton width={60} height={28} style={{ marginBottom: "32px" }} />

          {/* Info items - Horizontal layout */}
          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={60} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width="100%" height={36} variant="rectangular" />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={100} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width={100} height={16} />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={80} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width={120} height={16} />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={60} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width={110} height={16} />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={70} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width="100%" height={36} variant="rectangular" />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={70} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width="100%" height={36} variant="rectangular" />
            </div>
          </div>

          <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
              <Skeleton width={20} height={20} variant="circular" />
              <Skeleton width={80} height={16} />
            </div>
            <div style={styles.infoValue}>
              <Skeleton width={90} height={16} />
            </div>
          </div>

          <div style={styles.divider}></div>

          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <Skeleton width={50} height={18} />
              <Skeleton width={80} height={16} />
            </div>
            <Skeleton width={70} height={28} style={{ borderRadius: "14px" }} />
          </div>

          <div style={styles.divider}></div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <Skeleton width={80} height={18} />
              <Skeleton width={100} height={16} />
            </div>

            <div style={styles.commentCard}>
              <div style={styles.commentHeader}>
                <Skeleton width={40} height={40} variant="circular" />
                <div style={{ flex: 1 }}>
                  <Skeleton
                    width={110}
                    height={16}
                    style={{ marginBottom: "6px" }}
                  />
                  <Skeleton width={130} height={14} />
                </div>
              </div>
              <Skeleton
                width="100%"
                height={14}
                style={{ marginBottom: "6px" }}
              />
              <Skeleton
                width="95%"
                height={14}
                style={{ marginBottom: "6px" }}
              />
              <Skeleton width="75%" height={14} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticleDetailSkeleton;
